import { useState, useCallback } from 'react';
import { DormitoryHttp, HelpdeskHttp } from '@/services';
import { useUniversityContext, useUserContext } from '@/context';
import { createHelpdeskIssue } from '@/components/helpdesk/helpdeskUtils';
import { parseError } from '@/utils';
import { useTranslation } from 'react-i18next';

export default function useHelpdesk() {

  const { profile } = useUserContext();
  const { university } = useUniversityContext();

  const [t] = useTranslation(['common', 'helpdesk']);
  const [statuses, setStatuses] = useState<IdName[]>([]);
  const [statusesError, setStatusesError] = useState('');
  const [statusesLoading, setStatusesLoading] = useState(false);
  const [issues, setIssues] = useState<RedmineIssueEntity[]>([]);
  const [issuesTotal, setIssuesTotal] = useState(0);
  const [issuesError, setIssuesError] = useState('');
  const [issuesLoading, setIssuesLoading] = useState(true);
  const [issuesTypes, setIssuesTypes] = useState<{ value: string; label: string }[]>([]);
  const [typesLoading, setTypesLoading] = useState(false);
  const [typesError, setTypesError] = useState('');
  const [categoryError, setCategoryError] = useState('');
  const [categoryLoading, setCategoryLoading] = useState(false);
  const [categoryList, setCategoryList] = useState<IdName[]>([]);

  // private states
  const [project, setProject] = useState<RedmineProjectEntity>(null);
  const [dormitory, setDormitory] = useState<UserDormitoryEntity>(null);

  const fetchIssuesTypes = useCallback(async () => {
    try {
      setTypesError('');
      setTypesLoading(true);
      const response = await HelpdeskHttp.fetchCustomFields();
      const types = (response?.custom_fields ?? [])?.find(x => x.id === 155)?.possible_values || [];
      setIssuesTypes(types);
      return types;
    }
    catch (e) {
      setTypesError(parseError(e, t('common:errors.connection-error-try-again')));
      return [];
    }
    finally {
      setTypesLoading(false);
    }
  }, [t]);

  const fetchIssues = useCallback(async (page = 1, limit = 100) => {
    setIssuesLoading(true);
    setIssuesError('');
    try {
      const response = await HelpdeskHttp.fetchIssues(profile.username, page, limit);
      setIssues(response.issues);
      setIssuesTotal(response.total_count);
    }
    catch (e) {
      setIssuesError(parseError(e, t('common:errors.connection-error-try-again')));
    }
    setIssuesLoading(false);
  }, [profile?.username, t]);

  const fetchStatuses = useCallback(async () => {
    setStatusesError('');
    setStatusesLoading(true);
    setStatuses([]);
    try {
      const response = await HelpdeskHttp.fetchStatuses();
      setStatuses(response.issue_statuses);
    }
    catch (e) {
      setStatusesError(parseError(e, t('common:errors.connection-error-try-again')));
    }
    setStatusesLoading(false);
  }, [t]);

  const createIssue = useCallback(async ({ subject, description, categoryId, uploads = [] }) => {
    const issue = createHelpdeskIssue({
      profile,
      university,
      categoryId,
      subject,
      description,
      dormitory,
      projectId: project.id,
      uploads,
      t
    });
    return HelpdeskHttp.createIssue(issue);
  }, [profile, university, project, dormitory, t]);

  const fetchCategories = useCallback(async (typeId: string) => {
    setCategoryError('');
    setCategoryLoading(true);
    try {
      const projects = (await HelpdeskHttp.fetchProjects(typeId))?.projects;
      const { project, dormitory } = await getProjectForCategories(projects, typeId);
      setProject(project);
      setDormitory(dormitory);
      if (project === null) {
        return setCategoryError(t('helpdesk:errors.permission-category'));
      }
      const response = await HelpdeskHttp.fetchProjectCategories(project?.id || -1);
      setCategoryList(response?.issue_categories ?? []);
    }
    catch (e) {
      console.error(e);
      setCategoryLoading(false);
      setCategoryError(t('helpdesk:errors.permission-category'));
    }
    finally {
      setCategoryLoading(false);
    }
  }, [t]);

  return {
    issues,
    fetchIssues,
    issuesTotal,
    issuesLoading,
    issuesError,
    issuesTypes,
    typesError,
    typesLoading,
    categoryList,
    categoryError,
    categoryLoading,
    fetchCategories,
    fetchIssuesTypes,
    createIssue,
    fetchStatuses,
    statuses,
    statusesError,
    statusesLoading,
  };
}

// Private functions
async function getProjectForCategories(projects: RedmineProjectEntity[], typeId: number | string) {
  projects = projects?.filter(p => p.custom_fields?.find(cf => cf.id === 155 && Number(cf.value) === Number(typeId)));
  const dormitory = await DormitoryHttp.fetchUserDormitory();

  if (projects.length === 1) {
    return { project: projects[0], dormitory: dormitory?.payload };
  }

  if (projects.length > 1) {
    const dormitoryName = dormitory?.payload?.dormitoryRoom?.dormitory?.name;
    const project = projects.find(p => p.name === dormitoryName);
    return { project, dormitory: dormitory?.payload };
  }

  return { project: null, dormitory: null };
}
